package org.example.project.enginevertrieb

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.key
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import org.example.project.dashboard.DashboardTheme
import org.example.project.dashboard.POPUP_ENGINE_MODAL_Z_INDEX
import org.example.project.dashboard.PopupEngineCard
import org.example.project.enginetabular.TabularScreen
import org.example.project.utils.All
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

@Composable
internal fun VertriebIcpWerteDialog(
    onClose: () -> Unit,
    onChanged: (List<VertriebsindexApi.IcpEintrag>) -> Unit
) {
    var werte by remember { mutableStateOf<List<VertriebsindexApi.IcpEintrag>>(emptyList()) }
    var error by remember { mutableStateOf("") }
    var version by remember { mutableStateOf(0) }
    val scope = rememberCoroutineScope()

    suspend fun reload() {
        try {
            werte = VertriebsindexApi.loadIcpWerte().sortedBy { it.id.toIntOrNull() ?: Int.MAX_VALUE }
            onChanged(werte)
            error = ""
            version++
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            error = e.message ?: "ICP-Werte konnten nicht geladen werden."
        }
    }

    LaunchedEffect(Unit) { reload() }

    PopupEngineCard(
        windowKey = "vertrieb-icp-werte-verwalten",
        zIndex = POPUP_ENGINE_MODAL_Z_INDEX,
        widthPx = 880, heightPx = 640, minWidthPx = 560, minHeightPx = 420,
        centered = true, viewportAnchored = true, resizable = true,
        onClose = onClose
    ) {
        Div({
            attr("role", "dialog"); attr("aria-modal", "true")
            style {
                width(100.percent); height(100.percent); padding(18.px); boxSizing("border-box")
                display(DisplayStyle.Flex); flexDirection(FlexDirection.Column)
                color(DashboardTheme.TEXT_LIGHT); gap(10.px)
            }
        }) {
            Div({ style { textAlign("center"); fontSize(20.px); fontWeight("700") } }) { Text("ICP-Werte verwalten") }
            if (error.isNotBlank()) Div({ attr("role", "alert"); style { color(Color("#ffb4b4")); textAlign("center") } }) { Text(error) }
            key(version) {
                TabularScreen(
                    screenKey = "VertriebIcpWerte",
                    rollenKey = "Vertriebsindex",
                    title = null,
                    headers = listOf("#", "ICP"),
                    fixedLeadingColumns = listOf("#"),
                    models = werte.map { All(id = it.id, icp = it.icp) },
                    data = werte.map { it.id to listOf(it.id, it.icp) },
                    editable = true,
                    commitChangedRowsOnly = true,
                    includeMetadata = false,
                    allowImportButton = false,
                    allowExportButton = false,
                    allowSettingsButton = false,
                    createButtonLabel = "ICP-Wert anlegen",
                    createRow = { id -> listOf(id, "") },
                    nonEditableCols = setOf("#"),
                    onRefresh = { scope.launch { reload() } },
                    onCommit = {},
                    onCommitConfirmed = { rows, complete ->
                        scope.launch {
                            try {
                                val changed = rows.map { (rowId, columns) ->
                                    val knownId = rowId.takeIf { id -> werte.any { it.id == id } }.orEmpty()
                                    VertriebsindexApi.IcpEintrag(knownId, columns.getOrNull(1).orEmpty().trim())
                                }
                                werte = VertriebsindexApi.saveIcpWerte(changed).sortedBy { it.id.toIntOrNull() ?: Int.MAX_VALUE }
                                onChanged(werte)
                                error = ""
                                complete(true)
                                version++
                            } catch (e: CancellationException) {
                                complete(false)
                                throw e
                            } catch (e: Exception) {
                                error = e.message ?: "ICP-Werte konnten nicht gespeichert werden."
                                complete(false)
                            }
                        }
                    },
                    onDeleteRows = { ids ->
                        try {
                            werte = VertriebsindexApi.deleteIcpWerte(ids).sortedBy { it.id.toIntOrNull() ?: Int.MAX_VALUE }
                            onChanged(werte)
                            error = ""
                            version++
                        } catch (e: CancellationException) { throw e }
                        catch (e: Exception) {
                            val marker = Regex("vertrieb_icp_wert_in_verwendung:(\\d+)").find(e.message.orEmpty())
                            error = if (marker != null) {
                                "Löschen nicht möglich: Dieser ICP-Wert ist noch ${marker.groupValues[1]} Leads zugewiesen."
                            } else e.message ?: "ICP-Werte konnten nicht gelöscht werden."
                            throw e
                        }
                    }
                )
            }
        }
    }
}
